import { useState } from 'react';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { listEstimatedSalesByRecipe } from '../services/DashboardCRUD';
import { toast } from 'react-toastify';
import WarehouseFilter from '../../../components/WarehouseFilter';
import { Warehouse } from '../../stock/models/StockLine';

export default function RecipeSalesReport() {
  const intl = useIntl();
  const initialSince = new Date(new Date().setDate(new Date().getDate() + 7));
  const initialUntil = new Date(new Date().setDate(new Date().getDate() + 14));
  const [since, setSince] = useState(initialSince);
  const [until, setUntil] = useState(initialUntil);
  const [generatingSalesReport, setGeneratingSalesReport] = useState(false);
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [recipeSales, setRecipeSales] = useState<{
    [key: string]: number;
  } | null>(null);

  async function generateSalesReport() {
    try {
      setGeneratingSalesReport(true);
      const { data: sales } = await listEstimatedSalesByRecipe({
        since: moment(since).format('YYYY-MM-DD'),
        until: moment(until).format('YYYY-MM-DD'),
        warehouse,
      });
      setRecipeSales(sales);
    } catch (error) {
      toast.error(
        intl.formatMessage({ id: 'businessDashboard.recipeSalesReportError' })
      );
      console.warn(error);
    } finally {
      setGeneratingSalesReport(false);
    }
  }

  return (
    <>
      <h1 className="title mb-8">
        {intl.formatMessage({
          id: 'businessDashboard.generateRecipeSalesReport',
        })}
      </h1>
      <div className="d-flex gap-4 align-items-end">
        <DateRangeSelector
          initialSince={initialSince}
          initialUntil={initialUntil}
          onSelected={(since, until) => {
            setSince(since);
            setUntil(until);
          }}
          disableFutureDates={false}
          disabled={generatingSalesReport}
        />
        <WarehouseFilter
          value={warehouse}
          onChange={(warehouse) => setWarehouse(warehouse)}
          disabled={generatingSalesReport}
        />
        <button
          className="btn btn-primary mb-1"
          onClick={generateSalesReport}
          disabled={generatingSalesReport || !since || !until}
        >
          {generatingSalesReport && (
            <span className="spinner-border spinner-border-sm me-2" />
          )}
          {intl.formatMessage({
            id: 'businessDashboard.generateRecipeSalesReport',
          })}
        </button>
      </div>
      {!generatingSalesReport && recipeSales && (
        <section
          className="card shadow mt-8 p-10"
          style={{
            width: 'fit-content',
          }}
        >
          <h2 className="mb-8">
            {intl.formatMessage({ id: 'businessDashboard.recipeSales' })}
          </h2>
          {Object.keys(recipeSales).length > 0 ? (
            <div
              style={{
                maxHeight: '500px',
                overflowY: 'auto',
                boxShadow: 'inset 0 -12px 10px -10px rgba(0,0,0,0.15)',
              }}
            >
              <table className="table table-striped table-hover table-bordered w-600px">
                <thead className="table-light fw-bold">
                  <tr>
                    <th className="ps-4">
                      {intl.formatMessage({ id: 'businessDashboard.product' })}
                    </th>
                    <th className="pe-4">
                      {intl.formatMessage({ id: 'businessDashboard.units' })}
                    </th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {Object.keys(recipeSales).map((key, index) => (
                    <tr key={index} className="table-row">
                      <td className="ps-4">{key}</td>
                      <td className="pe-4">{recipeSales[key]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>{intl.formatMessage({ id: 'businessDashboard.noTopSales' })}</p>
          )}
        </section>
      )}
    </>
  );
}
